import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Terminal, ArrowRight } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1709626011485-6fe000ea2dbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBnZW9tZXRyeXxlbnwwfHx8YmxhY2t8MTc4Nzg0MjM5M3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@example.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/", { replace: true });
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block overflow-hidden border-r border-border">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-2 text-white">
            <Terminal className="w-5 h-5" />
            <span className="font-mono text-sm tracking-tight">claude-code / otlp</span>
          </div>
          <div>
            <p className="overline mb-4">telemetry command center</p>
            <h1 className="font-mono text-4xl lg:text-5xl font-semibold text-white leading-tight tracking-tight">
              Ingest. Normalize.
              <br />
              Observe usage.
            </h1>
            <p className="mt-6 text-muted-foreground text-sm max-w-md leading-relaxed">
              OpenTelemetry over HTTPS for Claude Code. Token, cost, session and raw-event
              visibility without a billing console.
            </p>
          </div>
          <div className="font-mono text-xs text-muted-foreground/70">
            POST /api/v1/metrics · POST /api/v1/logs
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm animate-fade-in" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8 text-white">
            <Terminal className="w-5 h-5" />
            <span className="font-mono text-sm">claude-code / otlp</span>
          </div>
          <p className="overline mb-2">dashboard access</p>
          <h2 className="text-2xl font-semibold tracking-tight mb-8">Sign in</h2>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="overline">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="font-mono bg-black/50 border-border rounded-none focus:ring-1 focus:ring-white h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="overline">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="font-mono bg-black/50 border-border rounded-none focus:ring-1 focus:ring-white h-11"
              />
            </div>

            {error && (
              <div className="text-sm text-destructive font-mono border border-destructive/40 bg-destructive/10 px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 rounded-none bg-white text-black hover:bg-white/90 font-medium group"
            >
              {loading ? "Authenticating…" : "Enter dashboard"}
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
